import React, { useCallback, useEffect, useRef, useState } from 'react';
import fp from 'lodash/fp';
import { StyledBreadCrumbs, StyledLink } from './styled';
import { Box } from '@mui/material';
import { Button } from '../button';
import { Tooltip } from '../tooltip';
import { IconArrowDropDown, IconMoreHoriz, ImageFolderSmallOn } from '@mzc-cloudplex/icons';
import { Menu } from '../menu';
import { IBreadcrumbsItemProps } from './types';

//font, 최소/최대 너비 등은 props로 전달 받아 사용할 수 있게 개선
const getTextWidth = (titles: string[]) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context) {
    context.font = '14px Noto Sans KR';
    const width = titles.reduce((acc, cur) => {
      let textWidth = context.measureText(cur).width;
      if (textWidth < 64) textWidth = 64;
      else if (textWidth > 170) textWidth = 170;
      return acc + textWidth + 30;
    }, 0);
    return width;
  }
  return 0;
};

const BreadCrumbItem = ({
  itemType,
  item,
  moveFolderDisabled,
  onClickMenuItem
}: IBreadcrumbsItemProps) => {
  const [breadcrumbItemWidth, setBreadcrumbItemWidth] = useState<number>(0);
  const breadcrumbRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((event) => {
      setBreadcrumbItemWidth(event[0].contentBoxSize[0].inlineSize);
    });
    if (breadcrumbRef.current) resizeObserver.observe(breadcrumbRef.current);

    return () => {
      if (breadcrumbRef.current) resizeObserver.unobserve(breadcrumbRef.current);
    };
  }, [item]);

  return itemType === 'normal' ? (
    <Tooltip title={breadcrumbItemWidth >= 154 ? item.name : ''}>
      <Button
        ref={breadcrumbRef}
        color={'secondary'}
        variant={'text'}
        size={'small'}
        onClick={item.onClick}
        sx={{
          maxWidth: `170px`,
          textAlign: `left`,
          span: {
            display: `block`,
            overflow: `hidden`,
            textOverflow: `ellipsis`,
            whiteSpace: `nowrap`
          }
        }}
      >
        {fp.get(`name`, item)}
      </Button>
    </Tooltip>
  ) : (
    <Tooltip title={breadcrumbItemWidth >= 154 ? item.name : ''}>
      <Menu
        ref={breadcrumbRef}
        id={'menu-button'}
        sx={{
          '.text': {
            display: `inline-flex`,
            alignItems: `center`
          },
          '.ellipsis': {
            maxWidth: `150px`,
            display: `inline-block`,
            overflow: `hidden`,
            textOverflow: `ellipsis`,
            whiteSpace: `nowrap`,
            marginRight: `4px`
          }
        }}
        menuWidth={160}
        placement={'center'}
        label={
          <>
            <span className={'ellipsis'}>{fp.get(`name`, item)}</span>{' '}
            <IconArrowDropDown size={8} />
          </>
        }
        color={'inherit'}
        size={'small'}
        listSize={'small'}
        list={[
          {
            label: 'New Folder',
            onClick: () => onClickMenuItem && onClickMenuItem('create'),
            clickClose: true
          },
          {
            label: 'Move',
            onClick: () => onClickMenuItem && onClickMenuItem('move'),
            buttonDisabled: moveFolderDisabled,
            clickClose: true,
            divider: true
          },
          {
            label: 'Rename',
            onClick: () => onClickMenuItem && onClickMenuItem('rename'),
            clickClose: true
          },
          {
            label: 'Delete',
            onClick: () => onClickMenuItem && onClickMenuItem('delete'),
            clickClose: true,
            divider: true
          }
        ]}
      />
    </Tooltip>
  );
};

export const BreadCrumbs = ({
  type,
  separator,
  depth,
  maxItems,
  icon,
  method,
  onClickMenuItem,
  ...restProps
}) => {
  const outsideBoxRef = useRef<HTMLDivElement>();
  const [boxWidth, setBoxWidth] = useState<number>(0);
  const [isCollapse, setIsCollapse] = useState<boolean>(false);
  const [size, setSize] = useState<number>(0);

  const handleClick = useCallback(
    (values) => {
      const { link } = values;

      if (link && method) {
        method(link);
      }
    },
    [method]
  );

  const getBreadcrumbs = useCallback(() => {
    if (!depth) return [];
    let breadcrumbs = [
      <BreadCrumbItem
        itemType={'menu'}
        item={depth[depth.length - 1]}
        onClickMenuItem={onClickMenuItem}
        moveFolderDisabled={depth.length === 1}
      />
    ];

    if (depth.length > 2) {
      if (isCollapse) {
        breadcrumbs = [
          <Menu
            id={'menu-button'}
            menuWidth={340}
            placement={'center'}
            type={'icon'}
            label={<IconMoreHoriz size={16} />}
            color={'inherit'}
            size={'small'}
            listSize={'medium'}
            list={depth.slice(1, depth.length).map((item) => ({
              icon: <ImageFolderSmallOn width={16} height={16} />,
              label: item.name,
              clickClose: true
            }))}
            useTooltip={true}
          />
        ].concat(breadcrumbs);
      } else {
        breadcrumbs = depth
          .slice(1, depth.length - 1)
          .map((item) => <BreadCrumbItem itemType={'normal'} item={item} />)
          .concat(breadcrumbs);
      }
    }
    if (depth.length > 1)
      breadcrumbs.unshift(<BreadCrumbItem itemType={'normal'} item={depth[0]} />);

    return breadcrumbs;
  }, [depth, isCollapse]);

  useEffect(() => {
    const resizeBoxObserver = new ResizeObserver((event) => {
      setBoxWidth(event[0].contentBoxSize[0].inlineSize);
    });
    if (outsideBoxRef.current) resizeBoxObserver.observe(outsideBoxRef.current);

    return () => {
      if (outsideBoxRef.current) resizeBoxObserver.unobserve(outsideBoxRef.current);
    };
  }, [size]);

  useEffect(() => {
    if (!boxWidth) return;
    if (boxWidth > size) setIsCollapse(false);
    else setIsCollapse(true);
  }, [boxWidth]);

  useEffect(() => {
    if (!depth?.length) return;

    setSize(getTextWidth(depth.map(({ name }) => name)));

    return () => setSize(0);
  }, [depth]);

  return (
    <Box display={'flex'} flexWrap={'nowrap'} ref={outsideBoxRef}>
      {icon && (
        <Box
          sx={{ display: `inline-flex`, margin: type === 'tree' ? `8px 4px 0 0` : `4px 8px 0 0` }}
        >
          {icon}
        </Box>
      )}
      <StyledBreadCrumbs
        sx={{ width: '100%' }}
        separator={separator}
        itemsBeforeCollapse={2}
        maxItems={(depth?.length ?? 0) + 1}
        // maxItems={maxItems ? maxItems : parentWidth.toFixed(0) <= breadcrumbWidth.toFixed(0) ? 4 : 100}
        {...restProps}
        aria-label='breadcrumb'
      >
        {type === 'tree'
          ? !fp.isEmpty(depth) && depth !== undefined && getBreadcrumbs()
          : !fp.isEmpty(depth) &&
            depth !== undefined &&
            depth.map((item, index) => (
              <StyledLink
                key={index}
                onClick={() => handleClick(item)}
                $link={fp.has(`link`, item)}
                style={{
                  cursor: fp.has(`link`, item) ? `pointer` : `default`
                }}
              >
                {item.icon && (
                  <Box sx={{ display: `inline-flex`, margin: `0 8px 0 0` }}>{item.icon}</Box>
                )}
                <span>{fp.get(`name`, item)}</span>
              </StyledLink>
            ))}
      </StyledBreadCrumbs>
    </Box>
  );
};
